import React, { useState, useEffect } from "react";
import { Trash2, AlertTriangle, ArrowLeft, Shield } from "lucide-react";
import { MainLayout } from "../components/MainLayout";
import { useApp } from "../components/AppContext";
import { useAccountDelete } from "../api/user/useAccountDelete";
import { useAccountDeletionValidation } from "../api/user/useAccountDeletionValidation";
import { AccountDeleteDialog } from "../components/AccountDeleteDialog";
import { AccountDeletionValidation } from "../components/AccountDeletionValidation";
import { logout } from "../api/auth/authUtils";

export function DeleteMyAccount() {
	const { setCurrentScreen } = useApp();
	const { deleteMyAccount, loading } = useAccountDelete();
	const { validateAccountDeletion, loading: validationLoading } = useAccountDeletionValidation();
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [confirmText, setConfirmText] = useState("");
	const [validation, setValidation] = useState<any>(null);
	const [validationChecked, setValidationChecked] = useState(false);
	const [deleteError, setDeleteError] = useState<string | null>(null);

	const handleBack = () => {
		setCurrentScreen("app-settings");
	};

	// Check validation on component mount
	useEffect(() => {
		checkValidation();
	}, []);

	const checkValidation = async () => {
		try {
			const result = await validateAccountDeletion();
			setValidation(result);
			setValidationChecked(true);
		} catch (error) {
			console.error("Validation check failed:", error);
			setValidationChecked(true);
		}
	};

	const handleDeleteClick = () => {
		// Only allow deletion if validation passes
		if (validation?.canDelete) {
			setDeleteError(null); // Clear any previous errors when opening dialog
			setShowConfirmDialog(true);
		} else {
			// Re-check validation if user tries to delete
			checkValidation();
		}
	};

	const handleConfirmDelete = async () => {
		if (confirmText.toLowerCase() !== "löschen") {
			return;
		}

		try {
			setDeleteError(null); // Clear any previous errors
			const result = await deleteMyAccount();
			console.log('Account deletion successful:', result);
			
			// Close dialog first
			setShowConfirmDialog(false);
			
			// Clear auth data and redirect to login
			// Small delay to allow dialog to close smoothly
			setTimeout(() => {
				logout();
			}, 500);
			
		} catch (error: any) {
			console.error("Löschung fehlgeschlagen:", error);
			
			// Set error message to show in dialog
			const errorMessage = error.message || "Unbekannter Fehler bei der Account-Löschung";
			setDeleteError(errorMessage);
			
			// Don't close dialog so user can see the error and retry
		}
	};

	const isConfirmValid = confirmText.toLowerCase() === "löschen";
	const canProceedToDelete = validationChecked && validation?.canDelete;
	const showValidationResults = validationChecked && validation && !validation.canDelete;

	return (
		<MainLayout showBack onBack={handleBack}>
			<div className="max-w-md mx-auto space-y-6">
				{/* Header */}
				<div className="text-center">
					<div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
						canProceedToDelete ? "bg-red-100" : "bg-orange-100"
					}`}>
						{canProceedToDelete ? (
							<Trash2 className="h-8 w-8 text-red-600" />
						) : (
							<Shield className="h-8 w-8 text-orange-600" />
						)}
					</div>
					<h1 className="text-2xl font-bold text-gray-900 mb-2">
						Account löschen
					</h1>
					<p className="text-gray-600">
						{canProceedToDelete 
							? "Möchten Sie Ihren Account permanent löschen?"
							: "Überprüfe Berechtigung zur Account-Löschung..."
						}
					</p>
				</div>

				{/* Validation Results */}
				{validationLoading ? (
					<AccountDeletionValidation
						canDelete={false}
						blockers={[]}
						message=""
						loading={true}
					/>
				) : validation ? (
					<AccountDeletionValidation
						canDelete={validation.canDelete}
						blockers={validation.blockers || []}
						message={validation.message}
					/>
				) : null}

				{/* Warning - Only show if deletion is allowed */}
				{canProceedToDelete && (
					<div className="bg-red-50 border border-red-200 rounded-lg p-4">
						<div className="flex items-start gap-3">
							<AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
							<div>
								<h3 className="font-medium text-red-800 mb-2">
									ACHTUNG: Diese Aktion ist irreversibel!
								</h3>
								<div className="text-sm text-red-700 space-y-1">
									<p>
										Wenn Sie Ihren Account löschen, werden <strong>alle</strong>{" "}
										Ihre Daten permanent entfernt:
									</p>
									<ul className="list-disc list-inside space-y-1 mt-2">
										<li>Ihr Profil und persönliche Daten</li>
										<li>Alle Event-Anmeldungen und Buchungen</li>
										<li>Verträge und Gehaltsabrechnungen</li>
										<li>Hochgeladene Dokumente</li>
										<li>Gruppenmitgliedschaften</li>
										<li>Kommunikationsverlauf</li>
									</ul>
									<p className="mt-2 font-medium">
										Diese Daten können <strong>nicht wiederhergestellt</strong>{" "}
										werden!
									</p>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Info - Only show if deletion is allowed */}
				{canProceedToDelete && (
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
						<h3 className="font-medium text-blue-800 mb-2">
							Vor der Löschung bedenken:
						</h3>
						<ul className="text-sm text-blue-700 space-y-1">
							<li>• Haben Sie alle wichtigen Daten heruntergeladen?</li>
							<li>• Sind alle laufenden Verträge abgeschlossen?</li>
							<li>• Gibt es offene Buchungen oder Events?</li>
						</ul>
					</div>
				)}

				{/* Action */}
				<div className="space-y-4">
					<button
						onClick={handleDeleteClick}
						disabled={!canProceedToDelete || validationLoading}
						className={`w-full font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 ${
							canProceedToDelete && !validationLoading
								? "bg-red-600 hover:bg-red-700 text-white cursor-pointer"
								: "bg-gray-300 text-gray-500 cursor-not-allowed"
						}`}
					>
						<Trash2 className="h-4 w-4" />
						{validationLoading 
							? "Überprüfe Berechtigung..."
							: canProceedToDelete 
								? "Meinen Account permanent löschen"
								: "Account kann nicht gelöscht werden"
						}
					</button>

					<button
						onClick={handleBack}
						className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
					>
						<ArrowLeft className="h-4 w-4" />
						Zurück zu den Einstellungen
					</button>
				</div>

				{/* Confirmation Dialog */}
				<AccountDeleteDialog
					isOpen={showConfirmDialog}
					onClose={() => {
						setShowConfirmDialog(false);
						setDeleteError(null); // Clear error when dialog closes
						setConfirmText(""); // Clear confirm text
					}}
					onConfirm={handleConfirmDelete}
					isLoading={loading}
					confirmText={confirmText}
					onConfirmTextChange={setConfirmText}
					errorMessage={deleteError || undefined}
				/>
			</div>
		</MainLayout>
	);
}